import React, { useMemo, useState } from "react";
import { submitPaperOptionOrder } from "../paperTrading/paperTradingApi";
import "./NinaOptionsPaperOrderPanel.css";

const ALLOWED_INTENTS = new Set(["buy_to_open", "sell_to_close"]);

function finitePositiveNumber(value) {
  if (String(value).trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
}

function positiveInteger(value) {
  const number = finitePositiveNumber(value);
  return number !== null && Number.isInteger(number) ? number : null;
}

function displayMoney(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function providerOrderId(response) {
  const payload = response?.data || response?.payload || response;
  return payload && typeof payload === "object" ? payload.id || null : null;
}

export default function NinaOptionsPaperOrderPanel({ result }) {
  const contractSymbol = String(result?.contractId || "").trim().toUpperCase();
  const contractVerified =
    result?.dataMode === "authenticated-indicative" &&
    result?.contractMultiplier === 100 &&
    /^[A-Z0-9]{1,6}\d{6}[CP]\d{8}$/.test(contractSymbol);
  const [quantity, setQuantity] = useState("1");
  const [positionIntent, setPositionIntent] = useState("buy_to_open");
  const [limitPrice, setLimitPrice] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const normalizedQuantity = positiveInteger(quantity);
  const normalizedLimitPrice = finitePositiveNumber(limitPrice);
  const estimatedPremium = useMemo(() => {
    if (
      positionIntent !== "buy_to_open" ||
      normalizedQuantity === null ||
      normalizedLimitPrice === null
    ) {
      return null;
    }
    return normalizedQuantity * normalizedLimitPrice * 100;
  }, [normalizedLimitPrice, normalizedQuantity, positionIntent]);

  const resetReview = () => {
    setReviewing(false);
    setAcknowledged(false);
    setError("");
    setSuccess("");
  };

  const reviewOrder = (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (!contractVerified) {
      setError(
        "Paper order unavailable: exact active contract metadata is not verified.",
      );
      return;
    }
    if (normalizedQuantity === null) {
      setError("Quantity must be a positive whole number of contracts.");
      return;
    }
    if (!ALLOWED_INTENTS.has(positionIntent)) {
      setError("Choose Buy to Open or Sell to Close.");
      return;
    }
    if (normalizedLimitPrice === null) {
      setError("Enter a positive manual limit price.");
      return;
    }
    setReviewing(true);
    setAcknowledged(false);
  };

  const submitOrder = async () => {
    if (!reviewing || !acknowledged || submitting) return;
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const response = await submitPaperOptionOrder({
        contract_symbol: contractSymbol,
        quantity: normalizedQuantity,
        position_intent: positionIntent,
        limit_price: normalizedLimitPrice,
        time_in_force: "day",
      });
      const orderId = providerOrderId(response);
      setSuccess(
        orderId
          ? `Alpaca Paper order submitted. Order ID: ${orderId}`
          : "Alpaca Paper order submitted.",
      );
      setReviewing(false);
      setAcknowledged(false);
    } catch (requestError) {
      setError(
        requestError?.message ||
          "The Alpaca Paper order could not be submitted. No live order was sent.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!contractVerified) {
    return (
      <section
        className="optionsPaperOrderPanel"
        aria-label="Options Paper order"
      >
        <strong>ALPACA PAPER ONLY · ORDER UNAVAILABLE</strong>
        <p>
          Exact authenticated contract metadata and the verified 100-share
          multiplier are required before this panel can prepare a paper order.
        </p>
      </section>
    );
  }

  return (
    <section
      className="optionsPaperOrderPanel"
      aria-labelledby={`paper-order-${contractSymbol}`}
    >
      <header>
        <div>
          <span>ALPACA PAPER ONLY · LIMIT / DAY</span>
          <h4 id={`paper-order-${contractSymbol}`}>Deliberate paper option order</h4>
        </div>
        <strong className="optionsPaperBadge">NO LIVE TRADING</strong>
      </header>
      <p>
        Scanner status remains <strong>WAIT</strong>. Indicative bid/ask is context
        only; enter your own limit price. This does not turn AI Assistant output into a
        recommendation.
      </p>

      {!reviewing ? (
        <form className="optionsPaperOrderForm" onSubmit={reviewOrder}>
          <label>
            <span>Exact OCC contract</span>
            <input value={contractSymbol} readOnly aria-readonly="true" />
          </label>
          <label>
            <span>Position intent</span>
            <select
              value={positionIntent}
              onChange={(event) => {
                setPositionIntent(event.target.value);
                resetReview();
              }}
            >
              <option value="buy_to_open">Buy to Open</option>
              <option value="sell_to_close">Sell to Close</option>
            </select>
          </label>
          <label>
            <span>Contracts</span>
            <input
              aria-label="Paper option contracts"
              inputMode="numeric"
              min="1"
              step="1"
              type="number"
              value={quantity}
              onChange={(event) => {
                setQuantity(event.target.value);
                resetReview();
              }}
            />
          </label>
          <label>
            <span>Manual limit price</span>
            <input
              aria-label="Paper option limit price"
              inputMode="decimal"
              min="0.01"
              step="0.01"
              type="number"
              value={limitPrice}
              onChange={(event) => {
                setLimitPrice(event.target.value);
                resetReview();
              }}
              placeholder="0.00"
            />
          </label>
          <div className="optionsPaperEstimate" role="status">
            {positionIntent === "buy_to_open"
              ? estimatedPremium === null
                ? "Estimated premium: enter contracts and limit price"
                : `Estimated maximum premium: ${displayMoney(estimatedPremium)}`
              : "Sell to Close can only reduce an existing Paper position."}
          </div>
          <button type="submit" className="optionsPaperPrimary">
            Review paper order
          </button>
        </form>
      ) : (
        <div
          className="optionsPaperReview"
          role="group"
          aria-label="Final paper order review"
        >
          <h5>Final paper-order review</h5>
          <dl>
            <div>
              <dt>Environment</dt>
              <dd>Alpaca Paper</dd>
            </div>
            <div>
              <dt>Contract</dt>
              <dd>{contractSymbol}</dd>
            </div>
            <div>
              <dt>Intent</dt>
              <dd>
                {positionIntent === "buy_to_open"
                  ? "Buy to Open"
                  : "Sell to Close"}
              </dd>
            </div>
            <div>
              <dt>Quantity</dt>
              <dd>{normalizedQuantity}</dd>
            </div>
            <div>
              <dt>Order</dt>
              <dd>Limit · Day</dd>
            </div>
            <div>
              <dt>Limit price</dt>
              <dd>{displayMoney(normalizedLimitPrice)}</dd>
            </div>
            {estimatedPremium !== null && (
              <div>
                <dt>Maximum premium</dt>
                <dd>{displayMoney(estimatedPremium)}</dd>
              </div>
            )}
          </dl>
          <label className="optionsPaperAcknowledge">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            <span>
              I confirm this is an Alpaca Paper order using my manual limit price,
              not a live trade or AI recommendation.
            </span>
          </label>
          <div className="optionsPaperReviewActions">
            <button
              type="button"
              onClick={() => resetReview()}
              disabled={submitting}
            >
              Edit order
            </button>
            <button
              type="button"
              className="optionsPaperPrimary"
              disabled={!acknowledged || submitting}
              onClick={submitOrder}
            >
              {submitting ? "Submitting to Alpaca Paper…" : "Submit Alpaca Paper order"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="optionsPaperMessage optionsPaperError" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="optionsPaperMessage optionsPaperSuccess" role="status">
          {success}
        </div>
      )}
    </section>
  );
}
